import { useEffect, useState } from 'react';

import {
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { RefreshRounded as RefreshIcon } from '@material-ui/icons';

import { User } from '../../types/user.type';
import { UserDiscount } from '../../types/user-discount.type';
import { DISCOUNT_TYPES, DiscountType } from '../../../shared/types/discount.type';
import { formatPrice } from '../../../shared/utils/format-price.util';
import { useGetUserDiscounts } from '../../hooks/get-user-discounts.hook';
import { useCreateUserDiscount } from '../../hooks/create-user-discount.hook';
import { TableBodySkeleton } from '../../../shared/components/Tables/TableBodySkeleton.component';
import StatusChip from '../../../shared/components/Chips/StatusChip.component';

const useStyles = makeStyles((theme) => ({
  root: {
    '& h4': {
      margin: '0 0 0.5rem 0',
      opacity: 0.7,
    },

    '& .MuiButton-root': {
      textTransform: 'none',
    },

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  headerCell: {
    fontWeight: 'bold',
  },
  discountCode: {
    fontWeight: 600,
  },
  amountCell: {
    fontVariantNumeric: 'tabular-nums',
  },
  emptyCell: {
    color: '#757575',
  },
  createCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1rem 0.75rem',
  },
  amountRow: {
    display: 'flex',
    gap: '1rem',

    '& > div:first-child': {
      flex: 2,
    },
    '& > div:last-child': {
      flex: 1,
    },
  },
  createButton: {
    alignSelf: 'flex-end',
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

const formatDiscountAmount = (discount: UserDiscount): string =>
  discount.reduction === DISCOUNT_TYPES.PERCENT ? `${discount.value}%` : formatPrice(discount.value);

interface Props {
  user: User;
}

export default function UserDiscountsTab({ user }: Props) {
  const classes = useStyles();

  const { isLoadingDiscounts, discounts, refreshDiscounts } = useGetUserDiscounts(user.id);
  const { isCreatingDiscount, getNextDiscountCode, createDiscount } = useCreateUserDiscount();

  const [code, setCode] = useState('');
  const [amount, setAmount] = useState<number>(0);
  const [reduction, setReduction] = useState<DiscountType>(DISCOUNT_TYPES.FLAT);

  const regenerateCode = async () => {
    const nextCode = await getNextDiscountCode();
    if (nextCode) setCode(nextCode);
  };

  useEffect(() => {
    regenerateCode();
    // eslint-disable-next-line
  }, []);

  const handleCreate = async () => {
    const created = await createDiscount({ name: code, value: amount, reduction, owner: user.id });
    if (created) {
      setAmount(0);
      await regenerateCode();
      await refreshDiscounts();
    }
  };

  return (
    <Grid container spacing={2} className={classes.root}>
      <Grid item xs={12} md={8}>
        <h4>{"Bons d'achat de l'utilisateur"}</h4>
        <Paper>
          <TableContainer>
            <Table size="small" aria-label="Bons d'achat de l'utilisateur">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headerCell}>Code</TableCell>
                  <TableCell className={classes.headerCell} align="right">
                    Montant
                  </TableCell>
                  <TableCell className={classes.headerCell} align="center">
                    Utilisations
                  </TableCell>
                  <TableCell className={classes.headerCell} align="right">
                    Statut
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoadingDiscounts && <TableBodySkeleton rows={3} columns={4} />}
                {!isLoadingDiscounts && discounts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className={classes.emptyCell}>
                      {"Aucun bon d'achat pour cet utilisateur : créez-en un depuis le formulaire ci-contre."}
                    </TableCell>
                  </TableRow>
                )}
                {!isLoadingDiscounts &&
                  discounts.map((discount) => (
                    <TableRow hover key={discount.id}>
                      <TableCell className={classes.discountCode}>{discount.name}</TableCell>
                      <TableCell align="right" className={classes.amountCell}>
                        {formatDiscountAmount(discount)}
                      </TableCell>
                      <TableCell align="center" className={classes.amountCell}>
                        {discount.count ?? 0}
                      </TableCell>
                      <TableCell align="right">
                        <StatusChip isActive={Boolean(discount.isActive)} />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>

      <Grid item xs={12} md={4}>
        <h4>{"Créer un bon d'achat"}</h4>
        <Paper className={classes.createCard}>
          <TextField
            label="Code"
            type="text"
            size="small"
            variant="outlined"
            fullWidth
            value={code}
            onChange={(event) => setCode(event.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" title="Régénérer le code" onClick={regenerateCode}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <div className={classes.amountRow}>
            <TextField
              label="Montant"
              type="number"
              size="small"
              variant="outlined"
              inputProps={{ min: 0 }}
              value={amount}
              onChange={(event) => setAmount(Number(event.target.value))}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">{reduction === DISCOUNT_TYPES.PERCENT ? '%' : '€'}</InputAdornment>
                ),
              }}
            />
            <FormControl variant="outlined" size="small">
              <InputLabel>Type</InputLabel>
              <Select
                native
                label="Type"
                value={reduction}
                onChange={(event) => setReduction(event.target.value as DiscountType)}
              >
                <option value={DISCOUNT_TYPES.FLAT}>€</option>
                <option value={DISCOUNT_TYPES.PERCENT}>%</option>
              </Select>
            </FormControl>
          </div>
          <Button
            size="small"
            variant="contained"
            className={classes.createButton}
            disabled={isCreatingDiscount || !code || amount <= 0}
            startIcon={isCreatingDiscount ? <CircularProgress size={16} /> : null}
            onClick={handleCreate}
          >
            Créer
          </Button>
        </Paper>
      </Grid>
    </Grid>
  );
}
